/* Panel interior: everything below the header. Deliberately quiet — white ground,
   neutral greys, the header's navy as the only colour, and the header's own system
   font so the panel reads as one piece. */

.chat-widget-panel {
  --esl-navy: #003da5;
  --esl-navy-hover: #00318a;
  --esl-text: #1d2433;
  --esl-muted: #5f6b7a;
  --esl-line: #e2e6ec;
  --esl-subtle: #f5f7fa;
  --esl-font: "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", Arial,
    sans-serif;
  /* 2:3, like OpenAI's support panel; frame.css still caps it to the viewport. */
  width: 400px;
  height: 600px;
}

/* ---- conversation area ---------------------------------------------------------- */

.chat-widget-messages {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  background: #fff;
  font-family: var(--esl-font);
  font-size: 14px;
  line-height: 1.5;
  color: var(--esl-text);
  scrollbar-width: thin;
  scrollbar-color: var(--esl-line) transparent;
}

/* ---- welcome -------------------------------------------------------------------- */

/* Centred in the empty panel, with room to breathe. */
.chat-widget-welcome {
  display: flex;
  flex-direction: column;
  margin-block: auto;
  padding: 8px 4px;
  text-align: center;
}
.chat-widget-welcome.hidden {
  display: none;
}
.chat-widget-welcome-title {
  margin: 0 0 6px;
  font-size: 21px;
  font-weight: 600;
  line-height: 1.3;
  text-wrap: balance;
  color: var(--esl-text);
}
.chat-widget-welcome-lede {
  margin: 0 0 24px;
  color: var(--esl-muted);
}

.chat-widget-action-buttons {
  display: flex;
  flex-direction: column;
  text-align: left;
  border: 1px solid var(--esl-line);
  border-radius: 8px;
  overflow: hidden;
}
.chat-widget-action-button {
  display: grid;
  grid-template-columns: 20px 1fr;
  column-gap: 12px;
  align-items: center;
  width: 100%;
  padding: 9px 14px;
  border: 0;
  background: #fff;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.12s;
}
.chat-widget-action-button + .chat-widget-action-button {
  border-top: 1px solid var(--esl-line);
}
.chat-widget-action-button:hover {
  background: var(--esl-subtle);
}
.chat-widget-action-button:focus-visible {
  outline: 2px solid var(--esl-navy);
  outline-offset: -2px;
}
.chat-widget-action-icon {
  grid-row: span 2;
  display: grid;
  place-items: center;
  color: var(--esl-navy);
}
.chat-widget-action-icon svg {
  width: 18px;
  height: 18px;
}
.chat-widget-action-label {
  font-weight: 600;
  line-height: 1.3;
}
.chat-widget-action-hint {
  font-size: 12.5px;
  line-height: 1.35;
  color: var(--esl-muted);
}

/* ---- messages ------------------------------------------------------------------- */

.chat-message {
  max-width: 85%;
  padding: 9px 12px 7px;
  border-radius: 10px;
  overflow-wrap: anywhere;
}
.user-message {
  align-self: flex-end;
  background: var(--esl-navy);
  color: #fff;
}
.bot-message-wrapper {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  max-width: 100%;
}
.bot-message-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 2px;
}
.bot-message-section {
  min-width: 0;
}
.bot-message {
  max-width: none;
  background: var(--esl-subtle);
}
.message-timestamp {
  margin-top: 2px;
  font-size: 11px;
  color: var(--esl-muted);
  text-align: right;
}
.user-message .message-timestamp {
  color: rgba(255, 255, 255, 0.7);
}

/* a refusal the customer can act on, e.g. "E-Sea portal sign-in required" */
.bot-message.bot-message-notice {
  background: #fff8e6;
  border: 1px solid #f2d38a;
  border-left: 4px solid #e0a100;
}
.bot-message-notice .bot-message-content > p:first-child strong { color: #7a5200; }

/* markdown inside bot replies */
.bot-message-content > :first-child { margin-top: 0; }
.bot-message-content > :last-child { margin-bottom: 0; }
.bot-message-content p { margin: 0 0 8px; }
.bot-message-content ul,
.bot-message-content ol { margin: 4px 0 8px; padding-left: 20px; }
.bot-message-content li { margin-bottom: 2px; }
.bot-message-content strong { font-weight: 600; }
.bot-message-content h1,
.bot-message-content h2,
.bot-message-content h3,
.bot-message-content h4 {
  margin: 10px 0 4px;
  font-size: 14px;
  font-weight: 600;
}
.bot-message-content a {
  color: var(--esl-navy);
  text-underline-offset: 2px;
}
.bot-message-content code {
  padding: 1px 4px;
  border-radius: 3px;
  background: #fff;
  font-size: 0.92em;
}
.bot-message-content pre {
  overflow-x: auto;
  padding: 8px 10px;
  border-radius: 6px;
  background: #fff;
}
.bot-message-content blockquote {
  margin: 6px 0;
  padding-left: 10px;
  border-left: 2px solid var(--esl-line);
  color: var(--esl-muted);
}
/* The opening warning of a yellow (.bot-message-notice) reply: plain text in the bubble,
   no box of its own. */
.bot-message-notice .bot-message-content > blockquote:first-child {
  margin: 0 0 8px;
  padding: 0;
  border: 0;
  color: #7a5200;
}
.bot-message-notice .bot-message-content > blockquote:first-child p { margin: 0; }
.bot-message-content hr {
  border: 0;
  border-top: 1px solid var(--esl-line);
  margin: 8px 0;
}
/* Schedules and charges come back as tables; they scroll inside the bubble. */
.bot-message-content table {
  display: block;
  overflow-x: auto;
  border-collapse: collapse;
  margin: 6px 0;
  font-size: 12.5px;
  line-height: 1.35;
}
.bot-message-content th,
.bot-message-content td {
  padding: 5px 8px 5px 0;
  border-bottom: 1px solid var(--esl-line);
  text-align: left;
  vertical-align: top;
  overflow-wrap: normal;
  word-break: normal;
}
.bot-message-content th {
  white-space: nowrap;
}
.bot-message-content th {
  font-weight: 600;
}

/* ---- end chat ------------------------------------------------------------------- */

.chat-widget-session-bar {
  display: flex;
  justify-content: flex-end;
  padding: 0 20px 4px;
  background: #fff;
  font-family: var(--esl-font);
}
.chat-widget-session-bar[hidden] {
  display: none;
}
.chat-widget-end-chat {
  padding: 3px 8px;
  border: 0;
  border-radius: 6px;
  background: none;
  font: inherit;
  font-size: 12.5px;
  color: var(--esl-muted);
  cursor: pointer;
}
.chat-widget-end-chat:hover {
  background: var(--esl-subtle);
  color: var(--esl-text);
}
.chat-widget-end-chat:focus-visible,
.chat-new-session-link:focus-visible {
  outline: 2px solid var(--esl-navy);
  outline-offset: 1px;
}

/* ---- waiting: "✻ Charting… 3s" ----------------------------------------------------- */

.thinking-indicator {
  display: none;
}
.progress-message {
  align-self: flex-start;
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 2px 2px 2px 32px;
  font-size: 14px;
}
/* The glyph steps through star shapes and breathes, like a spark being struck. */
.progress-glyph {
  width: 14px;
  color: var(--esl-navy);
  font-size: 15px;
  line-height: 1;
  text-align: center;
  animation: esl-breathe 1.2s ease-in-out infinite;
}
.progress-glyph::before {
  content: "✻";
  animation: esl-glyph 1.2s steps(1) infinite;
}
@keyframes esl-glyph {
  0% { content: "·"; }
  16% { content: "✢"; }
  33% { content: "✳"; }
  50% { content: "✶"; }
  66% { content: "✻"; }
  83% { content: "✽"; }
}
@keyframes esl-breathe {
  0%, 100% { opacity: 0.55; transform: scale(0.9); }
  50% { opacity: 1; transform: scale(1.1); }
}
/* A highlight sweeps through the word, left to right. */
.progress-text {
  font-weight: 600;
  color: var(--esl-muted);
  background: linear-gradient(
    90deg,
    var(--esl-muted) 0%,
    var(--esl-muted) 40%,
    var(--esl-navy) 50%,
    var(--esl-muted) 60%,
    var(--esl-muted) 100%
  );
  background-size: 250% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: esl-shimmer 2s linear infinite;
}
@keyframes esl-shimmer {
  from { background-position: 100% 0; }
  to { background-position: 0 0; }
}
.progress-elapsed {
  font-size: 12px;
  color: var(--esl-muted);
  font-variant-numeric: tabular-nums;
}

@media (prefers-reduced-motion: reduce) {
  .progress-glyph,
  .progress-glyph::before,
  .progress-text {
    animation: none;
  }
  .progress-text {
    -webkit-text-fill-color: currentColor;
    background: none;
  }
}

/* ---- notices -------------------------------------------------------------------- */

.chat-security-message {
  align-self: stretch;
  padding: 7px 10px;
  border: 1px solid var(--esl-line);
  border-radius: 6px;
  background: var(--esl-subtle);
  font-size: 13px;
}
.chat-warning { border-color: #ecd9a4; background: #fdf8ea; }
.chat-error { border-color: #efc3c3; background: #fdf1f1; color: #9b1c1c; }

/* ---- end-of-chat survey ----------------------------------------------------------- */

.chat-satisfaction-survey {
  align-self: stretch;
  padding: 14px;
  border: 1px solid var(--esl-line);
  border-radius: 8px;
  text-align: center;
}
.chat-survey-title {
  font-weight: 600;
}
.chat-survey-emojis {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin: 10px 0;
}
.chat-survey-emoji {
  cursor: pointer;
  border-radius: 50%;
}
.chat-survey-emoji svg {
  width: 32px;
  height: 32px;
  display: block;
}
.chat-survey-emoji.selected { box-shadow: 0 0 0 2px var(--esl-navy); }
.chat-survey-emoji.disabled { opacity: 0.35; pointer-events: none; }
.chat-survey-end-message,
.chat-survey-thank-you {
  font-size: 13px;
  color: var(--esl-muted);
}
.chat-new-session-link {
  color: var(--esl-navy);
}

/* ---- composer ------------------------------------------------------------------- */

.chat-widget-input {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 16px 0;
  padding: 6px 6px 6px 18px;
  border: 1px solid #d9dee6;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(29, 36, 51, 0.06);
  font-family: var(--esl-font);
  transition: border-color 0.12s, box-shadow 0.12s;
}
.chat-widget-input:focus-within {
  border-color: var(--esl-navy);
  box-shadow: 0 0 0 3px rgba(0, 61, 165, 0.12);
}
.chat-widget-input input {
  flex: 1;
  min-width: 0;
  height: 38px;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 14.5px;
  color: var(--esl-text);
}
.chat-widget-input input::placeholder {
  color: #8a94a3;
}
.chat-widget-input input:focus {
  outline: none;
}
.chat-widget-input button {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 50%;
  background: var(--esl-navy);
  color: #fff;
  cursor: pointer;
  transition: background-color 0.12s;
}
.chat-widget-input button:hover:not(:disabled) {
  background: var(--esl-navy-hover);
}
.chat-widget-input button:focus-visible {
  outline: 2px solid var(--esl-navy);
  outline-offset: 2px;
}
.chat-widget-input button:disabled {
  opacity: 0.35;
  cursor: default;
}
.chat-widget-input button svg {
  width: 16px;
  height: 16px;
}

.chat-widget-footer {
  flex-shrink: 0;
  padding: 10px 28px 14px;
  background: #fff;
  font-family: var(--esl-font);
  font-size: 11px;
  line-height: 1.45;
  color: var(--esl-muted);
  text-align: center;
}
.chat-widget-footer a {
  color: inherit;
}

/* ---- maximized ------------------------------------------------------------------ */

.chat-widget-panel.maximized .chat-widget-messages {
  padding: 28px max(20px, calc((100% - 620px) / 2));
}

